/**
 * Consulta de carteira BTC/ETH — direto do client, já que nenhuma das
 * duas fontes exige key (nada a esconder que justifique passar por
 * Cloud Function, ao contrário de whois/dns/ip/cve/sanctions, que
 * vivem no backend por outros motivos: cache de rate limit apertado,
 * key sensível, ou dataset grande demais pra trazer pro client).
 *
 * BTC: blockstream.info Esplora API (GET /api/address/:address).
 * Confirmado: github.com/Blockstream/esplora/blob/master/API.md
 *
 * ETH: eth.blockscout.com — instância DEDICADA do Blockscout pra
 * Ethereum mainnet (não api.blockscout.com, que é a nova API "Pro"
 * multi-chain e EXIGE key). A distinção importa: a instrução original
 * pedia "explorer público equivalente pra ETH, sem key" — isso só é
 * verdade pra instância per-chain dedicada, confirmado contra
 * blog.blockscout.com/switch-to-the-blockscout-pro-api/.
 */

const BLOCKSTREAM_BASE = 'https://blockstream.info/api';
const BLOCKSCOUT_ETH_BASE = 'https://eth.blockscout.com/api/v2';

const BTC_ADDRESS_PATTERN = /^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{20,90}$/;
const ETH_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;

interface BlockstreamAddressStats {
  funded_txo_count: number;
  funded_txo_sum: number;
  spent_txo_count: number;
  spent_txo_sum: number;
  tx_count: number;
}

interface BlockstreamAddressResponse {
  address: string;
  chain_stats: BlockstreamAddressStats;
  mempool_stats: BlockstreamAddressStats;
}

export interface BtcWalletResult {
  address: string;
  confirmedBalanceSats: number;
  unconfirmedBalanceSats: number;
  totalTxCount: number;
}

export async function lookupBtcWallet(address: string): Promise<BtcWalletResult> {
  if (!BTC_ADDRESS_PATTERN.test(address)) {
    throw new Error('Endereço BTC com formato inválido.');
  }

  const res = await fetch(`${BLOCKSTREAM_BASE}/address/${encodeURIComponent(address)}`);
  if (!res.ok) {
    throw new Error(`blockstream.info respondeu ${res.status}`);
  }

  const data = (await res.json()) as BlockstreamAddressResponse;

  return {
    address: data.address,
    confirmedBalanceSats: data.chain_stats.funded_txo_sum - data.chain_stats.spent_txo_sum,
    unconfirmedBalanceSats: data.mempool_stats.funded_txo_sum - data.mempool_stats.spent_txo_sum,
    totalTxCount: data.chain_stats.tx_count + data.mempool_stats.tx_count,
  };
}

interface BlockscoutAddressResponse {
  hash: string;
  coin_balance: string | null; // wei, como string (número grande demais pra number)
  transactions_count: number | null;
  is_contract: boolean;
  ens_domain_name: string | null;
}

export interface EthWalletResult {
  address: string;
  balanceWei: string;
  balanceEth: number;
  txCount: number | null;
  isContract: boolean;
  ensName: string | null;
}

const WEI_PER_ETH = 1_000_000_000_000_000_000n;

export async function lookupEthWallet(address: string): Promise<EthWalletResult> {
  if (!ETH_ADDRESS_PATTERN.test(address)) {
    throw new Error('Endereço ETH com formato inválido.');
  }

  const res = await fetch(`${BLOCKSCOUT_ETH_BASE}/addresses/${encodeURIComponent(address)}`);
  if (!res.ok) {
    throw new Error(`eth.blockscout.com respondeu ${res.status}`);
  }

  const data = (await res.json()) as BlockscoutAddressResponse;
  const balanceWei = data.coin_balance ?? '0';

  return {
    address: data.hash,
    balanceWei,
    // Divisão por BigInt perde casas decimais — aceitável pro painel
    // RECON (exibição aproximada), não para cálculo financeiro exato.
    balanceEth: Number(BigInt(balanceWei) / (WEI_PER_ETH / 1_000_000n)) / 1_000_000,
    txCount: data.transactions_count,
    isContract: data.is_contract,
    ensName: data.ens_domain_name,
  };
}
